import React, { ChangeEvent, useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import Table, { HeadsType, RowType } from "../../Shared/CustomTable";
import { LiveSpeedsModalProps } from "./types";
import EditableSpeed from "./EditableSpeed";
import { updateLiveSpeeds } from "store/actions/players";

type CellValidity = {
  isValid: boolean; // true if valid
  isEmpty: boolean; // true if empty
};

const LiveSpeedsModal = ({
  show,
  selectedPlayers,
  close,
  setSelectedPlayers,
  gameId,
  teamId,
  setUpdateSpeeds,
}: LiveSpeedsModalProps) => {
  const [rowValidity, setRowValidity] = useState<{
    [id: string]: {
      max: CellValidity;
      maxS: CellValidity;
    };
  }>({});
  useEffect(() => {
    if (Object.keys(rowValidity).length === 0 && selectedPlayers.length > 0) {
      const initialValidity: {
        [id: string]: {
          max: { isValid: boolean; isEmpty: boolean };
          maxS: { isValid: boolean; isEmpty: boolean };
        };
      } = {};

      selectedPlayers.forEach((p) => {
        initialValidity[p.id] = {
          max: { isValid: false, isEmpty: true },
          maxS: { isValid: false, isEmpty: true },
        };
      });

      setRowValidity(initialValidity);
    }
  }, [selectedPlayers, rowValidity]);
  const handleClose = () => {
    close();
  };
  const handleSave = () => {
    updateLiveSpeeds(selectedPlayers, teamId, gameId);
    if (setSelectedPlayers) {
      setSelectedPlayers([]);
    }
    if (setUpdateSpeeds) {
      setUpdateSpeeds(false);
    }
    close();
  };

  const editPlayerSpeed = (
    playerId: string,
    field: "max" | "maxS",
    value: string
  ) => {
    if (setSelectedPlayers) {
      setSelectedPlayers((prev) =>
        prev.map((p) => (p.id === playerId ? { ...p, [field]: value } : p))
      );
    }
  };
  const reportValidity = (
    playerId: string,
    field: "max" | "maxS",
    isValid: boolean,
    isEmpty: boolean
  ) => {
    setRowValidity((prev) => ({
      ...prev,
      [playerId]: {
        ...prev[playerId],
        [field]: { isValid, isEmpty },
      },
    }));
  };
  const enableSave = Object.values(rowValidity).every((row) => {
    const maxHasValue = !row.max.isEmpty;
    const maxSHasValue = !row.maxS.isEmpty;

    const maxInvalid = maxHasValue && !row.max.isValid;
    const maxSInvalid = maxSHasValue && !row.maxS.isValid;

    // Rule 1: no invalid values
    if (maxInvalid || maxSInvalid) return false;

    const maxValid = maxHasValue && row.max.isValid;
    const maxSValid = maxSHasValue && row.maxS.isValid;

    // Rule 2: at least one valid
    return maxValid || maxSValid;
  });
  const isRowInvalid = (playerId: string) => {
    const row = rowValidity[playerId];
    if (!row) return false;

    const maxHasValue = !row.max.isEmpty;
    const maxSHasValue = !row.maxS.isEmpty;

    const maxInvalid = maxHasValue && !row.max.isValid;
    const maxSInvalid = maxSHasValue && !row.maxS.isValid;

    // Case 1: if one invalid → error
    if (maxInvalid || maxSInvalid) return true;

    const maxValid = maxHasValue && row.max.isValid;
    const maxSValid = maxSHasValue && row.maxS.isValid;

    // Case 2: if none is valid → error
    return !(maxValid || maxSValid);
  };
  const headers: HeadsType<string, JSX.Element> = [
    {
      content: "PLAYER",
      style: { width: "146px", textAlign: "left" },
      isOrderly: true,
      class: "t-ll",
      isColumnFixedY: true,
    },
    {
      content: "MAX SPEED.",
      style: { width: "100px" },
      isOrderly: true,
      class: "t-ll",
      isColumnFixedY: true,
    },
    {
      content: "MAX SUST. SPEED",
      style: { width: "100px" },
      isOrderly: true,
      isColumnFixedY: true,
    },
    { style: { width: "46px" } },
  ];

  const rows: RowType[] = [];
  if (selectedPlayers) {
    selectedPlayers.forEach((player, index) => {
      const invalid = isRowInvalid(player.id);
      rows.push({
        columns: [
          {
            class: "t-ll",
            style: { width: "146px", textAlign: "left" },
            content: player.name,
          },
          {
            class: "tt-qq t-ll",
            content: player.max,
            style: { width: "100px", textAlign: "center" },
            print: () => (
              <EditableSpeed
                key={player.id + "max"}
                editable={true}
                playerId={player.id}
                isMaxS={false}
                text={player.max}
                editPlayerSpeed={editPlayerSpeed}
                reportValidity={reportValidity}
              />
            ),
          },
          {
            class: "tt-qq",
            content: player.maxS,
            style: { width: "100px", textAlign: "center" },
            print: () => (
              <EditableSpeed
                key={player.id + "maxS"}
                editable={true}
                playerId={player.id}
                isMaxS={true}
                text={player.maxS}
                editPlayerSpeed={editPlayerSpeed}
                reportValidity={reportValidity}
              />
            ),
          },
          {
            style: { width: "46px" },
            content: invalid && (
              <i
                className="mdi mdi-alert"
                style={{
                  color: "#D6151C",
                  justifyContent: "left",
                  fontSize: 23,
                  marginLeft: "11px",
                }}
              />
            ),
          },
        ],
        id: player.id,
      });
    });
  }
  return (
    <Modal
      show={show}
      onHide={handleClose}
      size="lg"
      aria-labelledby="contained-modal-title-vcenter"
      centered
      className="modal-content-10"
    >
      <Modal.Header closeButton>
        <Modal.Title id="contained-modal-title-vcenter">
          <label
            className="p-01"
            style={{ fontSize: "20px", fontWeight: "bold" }}
          >
            Live Speed Updates
          </label>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Table classTable="condensed-view" rows={rows} headers={headers} isLiveSpeed={true} />
      </Modal.Body>
      <Modal.Footer className="text-right footer-s">
        <button className="btn-transparet" onClick={handleClose}>
          Cancel
        </button>
        <button
          style={
            !enableSave
              ? {
                  opacity: 0.5,
                  cursor: "default",
                  pointerEvents: "none",
                  color: "#FFFFFF",
                }
              : {}
          }
          className="btn btn-blue"
          onClick={() => {
            handleSave();
          }}
        >
          Save
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default LiveSpeedsModal;
